import { useTranslation } from 'react-i18next';
import { ArrowRight } from 'lucide-react';

/**
 * Hero Component
 * 
 * Landing page hero section with Khmer greeting, title, subtitle, and call-to-action buttons.
 * Features:
 * - Bilingual support with Khmer greeting displayed prominently
 * - Gradient text effects for emphasis
 * - Gradient background with decorative pulsing blobs
 * - Two CTA buttons (primary and secondary) with hover effects
 * - Responsive design for mobile, tablet, and desktop
 * 
 * The hero section is the first thing visitors see and sets the tone for the
 * entire website with vibrant Cambodian-inspired colors and welcoming messaging.
 * 
 * @component
 * @example
 * ```tsx
 * <Hero />
 * ```
 */
export default function Hero() {
  const { t } = useTranslation('home');

  return (
    <section className="relative bg-gradient-to-br from-primary-100 via-white to-secondary-100 section-padding overflow-hidden">
      <div className="container-custom relative z-10">
        <div className="max-w-4xl mx-auto text-center">
          {/* Khmer Greeting */}
          <div className="mb-6">
            <span className="text-5xl md:text-6xl font-khmer bg-gradient-to-r from-primary-500 via-primary-600 to-accent-500 bg-clip-text text-transparent font-bold drop-shadow-sm">
              {t('hero.greeting')}
            </span>
          </div>

          {/* Title */}
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-display font-bold text-neutral-900 mb-6 text-balance">
            {t('hero.title')}
          </h1>

          {/* Subtitle */}
          <p className="text-lg md:text-xl text-neutral-700 mb-8 max-w-3xl mx-auto">
            {t('hero.subtitle')}
          </p>

          {/* Tagline */}
          <p className="text-base bg-gradient-to-r from-secondary-600 to-primary-600 bg-clip-text text-transparent font-semibold mb-10">
            {t('hero.tagline')}
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="#about"
              className="bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 text-white px-8 py-4 rounded-lg font-semibold shadow-lg hover:shadow-xl transition-all inline-flex items-center justify-center gap-2 transform hover:scale-105"
            >
              {t('hero.cta.primary')}
              <ArrowRight size={20} />
            </a>
            <a
              href="#services"
              className="bg-white border-2 border-primary-500 text-primary-600 hover:bg-primary-50 px-8 py-4 rounded-lg font-semibold shadow-md hover:shadow-lg transition-all inline-flex items-center justify-center transform hover:scale-105"
            >
              {t('hero.cta.secondary')}
            </a>
          </div>
        </div>
      </div>

      {/* Decorative Elements */}
      <div className="absolute top-0 left-0 w-96 h-96 bg-gradient-to-br from-primary-300 to-accent-300 rounded-full filter blur-3xl opacity-30 -z-10 animate-pulse"></div>
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-gradient-to-tl from-secondary-300 to-primary-200 rounded-full filter blur-3xl opacity-30 -z-10 animate-pulse" style={{ animationDelay: '1s' }}></div>
      <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-full h-full bg-gradient-to-r from-transparent via-white to-transparent opacity-40 -z-10"></div>
    </section>
  );
}
